import { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { DocumentDirectoryType, ObjectProposalGenerationType } from '@econtractor/types';
import { fetchCloudflareContentBlob } from 'api/apiCloudflare';
import { getProposalIsUahMode, PdfDocumentView } from 'modules/templates';
import { useSettings } from 'context/SettingsContext';
import ShareDocument from 'components/ShareLink';
import ESpinner from 'components/others/Spinner';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { getCurrencyI18nParams, withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { safeFloatParse } from 'utils/helpers/safeParsing';

interface TabDocumentProps {
    row: ObjectProposalGenerationType;
    active: boolean;
    onSendEmail: (row: ObjectProposalGenerationType) => void;
}

const DOCUMENT_TYPE_OPTIONS: DocumentDirectoryType[] = ['invoice', 'contract', 'act', 'other'];

const isGeneratedPdf = (row: ObjectProposalGenerationType) =>
    (row.source || 'object') === 'lead' || Number(row.dirProposalId || 0) > 0;

const formatDocumentAmount = (amount: number, isUsd: boolean) => {
    const value = safeFloatParse(amount || 0);
    if (isUsd) return format$Price(value);
    return value >= 0
        ? withLocalCurrency(formatPrice(value))
        : withLocalCurrency(formatPrice(-value), true);
};

const formatDocumentDate = (value: number | null | undefined) => {
    const millis = Number(value || 0);
    if (!Number.isFinite(millis) || millis <= 0) return '';
    const date = DT.fromMillis(millis);
    return date.isValid ? date.toFormat('dd.MM.yyyy') : '';
};

interface DocumentFileActionsProps {
    row: ObjectProposalGenerationType;
    fileUrl: string;
    isDeleted: boolean;
    canSend: boolean;
    onSendEmail: (row: ObjectProposalGenerationType) => void;
    className?: string;
}

const DocumentFileActions = ({
    row,
    fileUrl,
    isDeleted,
    canSend,
    onSendEmail,
    className = '',
}: DocumentFileActionsProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'common']);
    const openLabel = t('Document:edit.generate.logOpenFile');
    const emailLabel = t('documents.popup.sendEmail');

    return (
        <div className={`d-flex align-items-center justify-content-end flex-wrap gap-2 ${className}`.trim()}>
            <EToolTip title={openLabel}>
                <a
                    className="btn btn-sm btn-light-secondary icon-btn-24"
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={openLabel}
                >
                    <SolarIcon name="folder2-open" />
                </a>
            </EToolTip>
            <ShareDocument
                text={row.proposalName || ''}
                link={fileUrl}
                inputMessageDlg
                disabled={isDeleted}
            />
            <EToolTip title={emailLabel}>
                <Button
                    size="sm"
                    variant="light-secondary"
                    className="icon-btn-24"
                    disabled={!canSend}
                    onClick={() => onSendEmail(row)}
                    aria-label={emailLabel}
                >
                    <SolarIcon name="envelope" />
                </Button>
            </EToolTip>
        </div>
    );
};

interface UploadedDocumentInfoProps {
    row: ObjectProposalGenerationType;
    onSendEmail: (row: ObjectProposalGenerationType) => void;
}

const UploadedDocumentInfo = ({ row, onSendEmail }: UploadedDocumentInfoProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'common']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const isUahMode = getProposalIsUahMode(rate);
    const isUsd = Boolean(Number(row.proposalUsd || 0));
    const fileUrl = String(row.filePublicUrl || '').trim();
    const fileName = String(row.fileName || '').trim();
    const isDeleted = Boolean(Number(row.deleted || 0));
    const canSend = !isDeleted && Boolean(fileUrl);
    const typeKey = (row.proposalType || 'other') as DocumentDirectoryType;
    const typeOptions = DOCUMENT_TYPE_OPTIONS.includes(typeKey)
        ? DOCUMENT_TYPE_OPTIONS
        : [typeKey, ...DOCUMENT_TYPE_OPTIONS];

    return (
        <div className="pt-3">
            <Row className="g-2 mb-3">
                <Col xs={12} sm={8} className="min-w-0">
                    <Form.Group>
                        <Form.Label>{t('Document:edit.fields.proposalName')}</Form.Label>
                        <Form.Control readOnly value={row.proposalName || ''} />
                    </Form.Group>
                </Col>
                <Col xs={12} sm={4} className="min-w-0">
                    <Form.Group>
                        <Form.Label>{t('Document:edit.fields.proposalType')}</Form.Label>
                        <Form.Select disabled value={typeKey}>
                            {typeOptions.map((type) => (
                                <option key={type} value={type}>
                                    {t(`Document:documentTypes.${type}`, { defaultValue: type })}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="g-2 mb-3">
                <Col xs={12} sm={7} className="min-w-0">
                    <Form.Group>
                        <Form.Label>{t('Document:edit.fields.proposalNumber')}</Form.Label>
                        <Form.Control readOnly value={row.proposalNumber || ''} />
                    </Form.Group>
                </Col>
                <Col xs={12} sm={5} className="min-w-0">
                    <Form.Group>
                        <Form.Label>{t('Document:edit.fields.proposalDt')}</Form.Label>
                        <Form.Control readOnly value={formatDocumentDate(row.proposalDt)} />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>{t('Document:edit.fields.proposalComment')}</Form.Label>
                <Form.Control as="textarea" rows={3} readOnly value={row.generationComment || ''} />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>{t('Document:edit.generate.logAmount')}</Form.Label>
                <Form.Control readOnly value={formatDocumentAmount(row.proposalAmount, isUsd)} />
            </Form.Group>

            {!isUahMode ? (
                <div className="form-check form-switch mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`uploaded-document-usd-${row.ID}`}
                        checked={isUsd}
                        disabled
                        readOnly
                    />
                    <label className="form-check-label" htmlFor={`uploaded-document-usd-${row.ID}`}>
                        {t('Document:edit.general.usdLabel', getCurrencyI18nParams())}
                    </label>
                    <div className="small text-muted">
                        {isUsd
                            ? t('Document:edit.general.usdHintUsd', getCurrencyI18nParams())
                            : t('Document:edit.general.usdHintUah', {
                                  ...getCurrencyI18nParams(),
                                  rate: Number(rate || 0).toFixed(2),
                              })}
                    </div>
                </div>
            ) : null}

            <Form.Group className="mb-3">
                <Form.Label>{t('documents.upload.fileLabel')}</Form.Label>
                <Form.Control readOnly value={fileName} />
            </Form.Group>

            {fileUrl ? (
                <DocumentFileActions
                    row={row}
                    fileUrl={fileUrl}
                    isDeleted={isDeleted}
                    canSend={canSend}
                    onSendEmail={onSendEmail}
                />
            ) : null}
        </div>
    );
};

const TabDocument = ({ row, active, onSendEmail }: TabDocumentProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'common']);
    const fileUrl = String(row.filePublicUrl || '').trim();
    const fileName = String(row.fileName || '').trim();
    const isDeleted = Boolean(Number(row.deleted || 0));
    const showPreview = isGeneratedPdf(row) && Boolean(fileUrl);
    const canSend = !isDeleted && Boolean(fileUrl);
    const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
    const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [rendered, setRendered] = useState(false);

    useEffect(() => {
        if (!active || !showPreview) return;

        let cancelled = false;
        setPdfBytes(null);
        setRendered(false);
        setPhase('loading');

        const load = async () => {
            const blob = await fetchCloudflareContentBlob(fileUrl);
            if (!blob) return null;
            return new Uint8Array(await blob.arrayBuffer());
        };

        load()
            .then((bytes) => {
                if (cancelled) return;
                if (!bytes?.byteLength) {
                    setPdfBytes(null);
                    setPhase('error');
                    return;
                }
                setPdfBytes(bytes);
                setPhase('ready');
            })
            .catch(() => {
                if (cancelled) return;
                setPdfBytes(null);
                setPhase('error');
            });

        return () => {
            cancelled = true;
        };
    }, [active, fileUrl, showPreview]);

    const busy = showPreview && active && phase !== 'error' && !(phase === 'ready' && rendered);

    if (!isGeneratedPdf(row)) {
        return <UploadedDocumentInfo row={row} onSendEmail={onSendEmail} />;
    }

    return (
        <div className="pt-3">
            {!fileUrl && !fileName ? <div className="text-muted mb-3">-</div> : null}

            {fileUrl ? (
                <DocumentFileActions
                    row={row}
                    fileUrl={fileUrl}
                    isDeleted={isDeleted}
                    canSend={canSend}
                    onSendEmail={onSendEmail}
                    className="mb-3"
                />
            ) : null}

            {showPreview ? (
                <div className="proposal-pdf-preview-wrap">
                    {busy ? (
                        <div className="py-5">
                            <ESpinner />
                        </div>
                    ) : null}
                    {phase === 'error' ? (
                        <p className="text-danger mb-0">{t('common:messError')}</p>
                    ) : null}
                    {phase === 'ready' && pdfBytes ? (
                        <PdfDocumentView
                            data={pdfBytes}
                            onError={() => setPhase('error')}
                            onReady={() => setRendered(true)}
                        />
                    ) : null}
                </div>
            ) : fileName ? (
                <div className="text-body">{fileName}</div>
            ) : null}
        </div>
    );
};

export default TabDocument;
